import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
import { useReducedMotion } from '@/composables/useReducedMotion';

/**
 * IntersectionObserver-based reveal для секций.
 *
 * Возвращает:
 *  - `target` — template-ref, его нужно навесить на отслеживаемый элемент
 *    (`<div :ref="target">…</div>`);
 *  - `isRevealed` — boolean ref, переходит в true при пересечении с
 *    viewport (или сразу при reduced-motion, или если IO недоступен).
 *
 * Раскрывается ровно один раз — после этого наблюдатель отключается.
 */
export interface UseScrollRevealOptions {
  readonly threshold?: number | readonly number[];
  readonly rootMargin?: string;
  /** Если true (по умолчанию) — после раскрытия наблюдатель отключается. */
  readonly once?: boolean;
}

export interface UseScrollRevealReturn {
  readonly target: Ref<HTMLElement | null>;
  readonly isRevealed: Ref<boolean>;
}

export function useScrollReveal(options: UseScrollRevealOptions = {}): UseScrollRevealReturn {
  const target = ref<HTMLElement | null>(null);
  const isRevealed = ref<boolean>(false);
  const reduced = useReducedMotion();
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    if (reduced.value) {
      isRevealed.value = true;
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      isRevealed.value = true;
      return;
    }

    const el = target.value;
    if (!el) {
      isRevealed.value = true;
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            isRevealed.value = true;
            if (options.once !== false) {
              observer?.disconnect();
              observer = null;
            }
            break;
          }
        }
      },
      {
        threshold:
          typeof options.threshold === 'number'
            ? options.threshold
            : options.threshold
              ? ([...options.threshold] as number[])
              : 0.15,
        rootMargin: options.rootMargin ?? '0px 0px -10% 0px',
      },
    );

    observer.observe(el);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return { target, isRevealed };
}
